import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useRoutes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import PlatformLayout from './components/PlatformLayout.jsx';
import { PublicOnlyRoute, RequireAuth } from './components/RouteGuards.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import CreateGamePage from './pages/CreateGamePage.jsx';
import GamesListPage from './pages/GamesListPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import PasswordResetPage from './pages/PasswordResetPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import ReferralProgramPage from './pages/ReferralProgramPage.jsx';
import ProfileSettingsPage from './pages/ProfileSettingsPage.jsx';
import MyCardsPage from './pages/MyCardsPage.jsx';
import CreateDeckPage from './pages/CreateDeckPage.jsx';
import GameSettingsPage from './pages/GameSettingsPage.jsx';
import { ConductorGamePage, PlayerGamePage } from './pages/GamePage.jsx';
import MyBoardsPage from './pages/MyBoardsPage.jsx';
import DeckDetailsPage from './pages/DeckDetailsPage.jsx';
import GameInvitePage from './pages/GameInvitePage.jsx';
import SecuritySettingsPage from './pages/SecuritySettingsPage.jsx';
import BalancePage from './pages/BalancePage.jsx';
import PaymentReturnPage from './pages/PaymentReturnPage.jsx';
import ThanksForPaymentPage from './pages/ThanksForPaymentPage.jsx';
import PricesPage from './pages/PricesPage.jsx';
import i18n from './i18n.js';
import { apiUrl } from './config/api';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  buildLocalizedUrl,
  getStoredLanguage,
  getFreshAuthLanguage,
  persistLanguage,
  resolveLanguagePriority,
  resolveLanguageFromParam,
  stripLanguagePrefix,
} from './utils/languageRouting.js';

const LocalizedRoutes = () => useRoutes([
  {
    path: 'dashboard',
    element: (
      <RequireAuth>
        <PlatformLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="create-game" replace /> },
      { path: 'create-game', element: <CreateGamePage /> },
      { path: 'games/group/:id/edit', element: <CreateGamePage mode="edit" /> },
      { path: 'games/individual', element: <GamesListPage type="individual" /> },
      { path: 'games/group', element: <GamesListPage type="group" /> },
      { path: 'games/personal', element: <GamesListPage type="personal" /> },
      { path: 'sessions', element: <Navigate to="../games/group" replace /> },
      { path: 'sessions/:id', element: <Navigate to="../games/group" replace /> },
      { path: 'settings/profile', element: <ProfileSettingsPage /> },
      { path: 'settings/security', element: <SecuritySettingsPage /> },
      { path: 'settings/game', element: <GameSettingsPage /> },
      { path: 'my-cards', element: <MyCardsPage /> },
      { path: 'my-cards/:deckId', element: <DeckDetailsPage /> },
      { path: 'my-boards', element: <MyBoardsPage /> },
      { path: 'decks/create', element: <CreateDeckPage /> },
      { path: 'balance', element: <BalancePage /> },
      { path: 'prices', element: <PricesPage /> },
      { path: 'referral', element: <ReferralProgramPage /> },
    ],
  },
  { path: 'payment/wayforpay/return', element: <PaymentReturnPage /> },
  { path: 'auth/impersonate', element: <ImpersonationLoginPage /> },
  {
    path: 'game/:id',
    element: (
      <RequireAuth>
        <ConductorGamePage />
      </RequireAuth>
    ),
  },
  { path: 'room/:id', element: <PlayerGamePage /> },
  { path: 'gamepage/:roomId', element: <GameInvitePage /> },
  { index: true, element: <Navigate to="dashboard/create-game" replace /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

const PublicFallbackPage = () => <Navigate to={buildLocalizedUrl('/login', DEFAULT_LANGUAGE)} replace />;


const ImpersonationLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyAuthData } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const consumeImpersonationToken = async () => {
      const params = new URLSearchParams(location.search || '');
      const token = (params.get('token') || '').trim();

      if (!token) {
        if (!cancelled) {
          setError('Ссылка для быстрого входа недействительна.');
        }
        return;
      }

      try {
        const response = await fetch(apiUrl('/api/auth/impersonation/consume'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || 'Не удалось выполнить вход под пользователем.');
        }

        applyAuthData(payload, false, { isolatedSession: true });
        navigate('/dashboard/create-game', { replace: true });
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Не удалось выполнить вход под пользователем.');
        }
      }
    };

    consumeImpersonationToken();
    return () => {
      cancelled = true;
    };
  }, [applyAuthData, location.search, navigate]);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Быстрый вход</h1>
        {error ? <p className="auth-error">{error}</p> : <p className="auth-info">Выполняем авторизацию…</p>}
      </div>
    </div>
  );
};

const BROWSER_GAME_ROOM_TITLE = 'Leela Space';

const BrowserTabTitleManager = () => {
  const location = useLocation();
  const { t } = useTranslation('common');

  useEffect(() => {
    const isGameRoomRoute = /\/(game|room)\//.test(location.pathname);
    document.title = isGameRoomRoute ? BROWSER_GAME_ROOM_TITLE : t('platform.dashboardTitle');
  }, [location.pathname, t]);

  return null;
};

const LanguageLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { user } = useAuth();

  const storedLanguage = getStoredLanguage();
  const freshAuthLanguage = getFreshAuthLanguage();
  const profileLanguage = resolveLanguageFromParam(user?.uiLanguage);
  const languageFromUrl = SUPPORTED_LANGUAGES.includes(lang) ? lang : null;
  const resolvedLanguage = useMemo(
    () =>
      resolveLanguagePriority(
        languageFromUrl,
        profileLanguage,
        freshAuthLanguage,
        storedLanguage,
        i18n.language,
        DEFAULT_LANGUAGE,
      ),
    [freshAuthLanguage, languageFromUrl, profileLanguage, storedLanguage, i18n.language],
  );

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const pathWithoutLangSegment = languageFromUrl ? `/${pathSegments.slice(1).join('/')}` || '/' : location.pathname;
  const sanitizedPath = stripLanguagePrefix(pathWithoutLangSegment);
  const needsRedirect = !languageFromUrl || languageFromUrl !== resolvedLanguage;

  useEffect(() => {
    if (needsRedirect) {
      const targetUrl = buildLocalizedUrl(sanitizedPath, resolvedLanguage, location.search, location.hash);
      console.debug('[LL] redirect to resolved language', {
        from: `${location.pathname}${location.search}${location.hash}`,
        to: targetUrl,
        languageFromUrl,
        resolvedLanguage,
      });
      navigate(targetUrl, { replace: true });
      return;
    }

    if (i18n.language !== resolvedLanguage) {
      i18n.changeLanguage(resolvedLanguage);
    }
    persistLanguage(resolvedLanguage);
  }, [
    needsRedirect,
    sanitizedPath,
    resolvedLanguage,
    location.pathname,
    location.search,
    location.hash,
    languageFromUrl,
    navigate,
    i18n,
  ]);

  if (needsRedirect) {
    return null;
  }

  return <LocalizedRoutes />;
};

const AuthLanguageLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const storedLanguage = getStoredLanguage();
  const freshAuthLanguage = getFreshAuthLanguage();
  const profileLanguage = resolveLanguageFromParam(user?.uiLanguage);
  const languageFromUrl = SUPPORTED_LANGUAGES.includes(lang) ? lang : null;
  const resolvedLanguage = useMemo(
    () =>
      resolveLanguagePriority(
        languageFromUrl,
        profileLanguage,
        freshAuthLanguage,
        storedLanguage,
        i18n.language,
        DEFAULT_LANGUAGE,
      ),
    [freshAuthLanguage, languageFromUrl, profileLanguage, storedLanguage, i18n.language],
  );

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const pathWithoutLangSegment = languageFromUrl ? `/${pathSegments.slice(1).join('/')}` || '/' : location.pathname;
  const sanitizedPath = stripLanguagePrefix(pathWithoutLangSegment);
  const needsRedirect = !languageFromUrl || languageFromUrl !== resolvedLanguage;

  useEffect(() => {
    if (needsRedirect) {
      const targetUrl = buildLocalizedUrl(sanitizedPath, resolvedLanguage, location.search, location.hash);
      console.debug('[AUTH-LANG] redirect', {
        from: `${location.pathname}${location.search}${location.hash}`,
        to: targetUrl,
        languageFromUrl,
        resolvedLanguage,
      });
      navigate(targetUrl, { replace: true });
      return;
    }

    if (i18n.language !== resolvedLanguage) {
      i18n.changeLanguage(resolvedLanguage);
    }
    persistLanguage(resolvedLanguage);
  }, [
    needsRedirect,
    resolvedLanguage,
    sanitizedPath,
    location.pathname,
    location.search,
    location.hash,
    languageFromUrl,
    navigate,
    i18n,
  ]);

  if (needsRedirect) {
    return null;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserTabTitleManager />
      <Routes>
        <Route
          path="/:lang/login"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <LoginPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />
        <Route
          path="/login"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <LoginPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />
        <Route
          path="/:lang/password-reset"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <PasswordResetPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />
        <Route
          path="/password-reset"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <PasswordResetPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />
        <Route
          path="/:lang/register"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <RegisterPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />
        <Route
          path="/register"
          element={(
            <PublicOnlyRoute>
              <AuthLanguageLayout>
                <RegisterPage />
              </AuthLanguageLayout>
            </PublicOnlyRoute>
          )}
        />

        <Route
          path="/:lang/thanks-for-the-payment"
          element={(
            <AuthLanguageLayout>
              <ThanksForPaymentPage />
            </AuthLanguageLayout>
          )}
        />
        <Route path="/thanks-for-the-payment" element={<ThanksForPaymentPage />} />

        <Route path="/:lang/*" element={<LanguageLayout />} />
        <Route path="*" element={<PublicFallbackPage />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
